import { useRef, useState, ReactNode, useEffect } from 'react';
import Styled from './CardCarousel.styled';
import { Icons } from '@tajawal/ct-web-design-system';
import { buildTestId } from '@/utils/helpers.utils';
import _min from 'lodash/min';

type CardCarouselProps = {
  children: ReactNode | ReactNode[];
  title?: ReactNode;
  testIdPrefix: string;
  className?: string;
  miscellaneousContent?: ReactNode;
  hasOverlayArrows?: boolean;
  shouldInitialize?: boolean;
  cardWidth?: number;
  shouldDarkenArrow?: boolean;
  activeIndex?: number;
};

/** TODO
 * - This implementation will work for all our existing case, but this is not a perfect carousal solution.
 * - Currently we have to pass cardWith to cardcarousel to make it work perfectly. Dynamic calculation of overflow/width
 *   of next and previous card would make the carousel more generic.
 */
export function CardCarousel({
  children,
  title = '',
  testIdPrefix,
  className = '',
  miscellaneousContent = '',
  hasOverlayArrows = false,
  shouldInitialize = true,
  cardWidth,
  shouldDarkenArrow = false,
  activeIndex: index = 0,
}: CardCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const leftMarkerRef = useRef<HTMLDivElement>(null);
  const rightMarkerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef(null);

  const [isNextScrollDisabled, setIsNextScrollDisabled] = useState(false);
  const [isPrevScrollDisabled, setIsPrevScrollDisabled] = useState(true);
  const [isSliderButtonVisble, setIsSliderButtonVisible] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  useEffect(() => {
    if (containerWidth && contentWidth && index > -1) {
      const maxIndex = Math.ceil(Math.abs(contentWidth - containerWidth) / (cardWidth + 16));
      setActiveIndex(_min([maxIndex, index]));
    }
  }, [index, containerWidth, contentWidth]);

  useEffect(() => {
    if (shouldInitialize) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          setContainerWidth((prev) => entries?.[0]?.rootBounds.width || prev);
          entries.forEach((entry) => {
            const { target, rootBounds, boundingClientRect, isIntersecting } = entry;
            if (target.ariaLabel.includes('left')) {
              setIsPrevScrollDisabled(isIntersecting);
            }
            if (target.ariaLabel.includes('right')) {
              setIsNextScrollDisabled(isIntersecting);
            }
            if (target.ariaLabel.includes('content')) {
              setIsSliderButtonVisible(rootBounds.width <= boundingClientRect.width);
              setContentWidth(boundingClientRect.width);
            }
          });
        },
        { root: containerRef.current }
      );
      observerRef.current.observe(leftMarkerRef.current);
      observerRef.current.observe(rightMarkerRef.current);
      observerRef.current.observe(contentRef.current);
    }

    return () => {
      if (shouldInitialize) {
        observerRef?.current?.disconnect();
      }
    };
  }, [shouldInitialize]);

  const handleScrollToNext = () => {
    setActiveIndex(activeIndex + 1);
  };

  const handleScrollToPrevious = () => {
    setActiveIndex(activeIndex - 1);
  };

  const sliderArrow = isSliderButtonVisble ? (
    <Styled.Slider>
      <Styled.ArrowButton
        data-testid={buildTestId(testIdPrefix, 'Slider-Prev')}
        $isDisabled={isPrevScrollDisabled}
        onClick={!isPrevScrollDisabled ? handleScrollToPrevious : undefined}
        $hasOverlayArrows={hasOverlayArrows}
        $isDark={shouldDarkenArrow}
      >
        <Icons.ChevronBackward width="16px" />
      </Styled.ArrowButton>
      <Styled.ArrowButton
        data-testid={buildTestId(testIdPrefix, 'Slider-Next')}
        $isDisabled={isNextScrollDisabled}
        onClick={!isNextScrollDisabled ? handleScrollToNext : undefined}
        $hasOverlayArrows={hasOverlayArrows}
        $isDark={shouldDarkenArrow}
      >
        <Icons.ChevronForward width="16px" />
      </Styled.ArrowButton>
    </Styled.Slider>
  ) : null;

  return (
    <Styled.Container className={className}>
      {(title || !hasOverlayArrows) && (
        <Styled.Header>
          {title}
          {!hasOverlayArrows && sliderArrow}
        </Styled.Header>
      )}
      {miscellaneousContent}
      <div style={{ position: 'relative' }}>
        {hasOverlayArrows && sliderArrow}
        <div ref={containerRef}>
          <Styled.Content
            $index={activeIndex}
            $cardWidth={cardWidth || containerWidth}
            $contentWidth={contentWidth}
            $containerWidth={containerWidth}
          >
            <div ref={leftMarkerRef} aria-label="left-marker" />
            <div ref={contentRef} aria-label="content-marker">
              {children}
            </div>
            <div ref={rightMarkerRef} aria-label="right-marker" />
          </Styled.Content>
        </div>
      </div>
    </Styled.Container>
  );
}
